/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    Package,
    Trophy,
    Star,
    ChevronLeft,
    ChevronRight,
    CheckCircle2,
    LogIn,
    UserPlus,
    LogOut,
    Mail,
    Lock,
    Eye,
    EyeOff,
    Cloud,
    Laptop,
    AlertCircle,
    RefreshCw,
    Share2,
    Copy,
    Check,
    X
} from 'lucide-react';
import { ALL_STICKERS, STICKER_MAP, TEAMS, CONF_META, CONF_ORDER, POS_ICON, ACHIEVEMENTS } from './constants';
import { Sticker } from './types';
import { StickerCard, EmptySlot } from './components/StickerItem';
import { Navigation } from './components/Navigation';
import { UserProfile } from './components/UserProfile';
import { FriendsPage } from './components/FriendsPage';
import { StickerShop } from './components/StickerShop';
import { GroupsPage } from './components/GroupsPage';
import { SchedulesPage } from './components/SchedulesPage';
import { FootballQuiz } from './components/FootballQuiz';
import { getLocalDateString } from './components/fallbackQuestions';
import { AIAgentBot } from './components/AIAgentBot';
import Footer from './components/Footer';
import { LANGUAGES, LanguageCode, TRANSLATIONS } from './translations';
import {
    auth,
    db,
    storage,
    googleProvider,
    signInWithPopup,
    signOut,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    updateProfile,
    doc,
    getDoc,
    setDoc,
    FirebaseUser,
    handleFirestoreError,
    OperationType,
    isFirestoreQuotaError,
    registerQuotaExceededListener,
    notifyQuotaExceeded
} from './firebase';
import { collection, query, where, onSnapshot, deleteDoc } from 'firebase/firestore';
import { toPng } from 'html-to-image';
import { ref as sRef, uploadString, getDownloadURL } from 'firebase/storage';

export default function App() {
    const [lang, setLang] = useState<LanguageCode>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('paganini-lang');
            if (saved && saved in TRANSLATIONS) return saved as LanguageCode;
        }
        return 'en';
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('paganini-lang', lang);
        }
    }, [lang]);

    const t = (key: string) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS['en']?.[key] || key;

    const [view, setView] = useState<'cover' | 'album' | 'team' | 'pack' | 'profile' | 'friends' | 'shop' | 'groups' | 'schedules' | 'quiz'>('cover');
    const [quizHistory, setQuizHistory] = useState<Record<string, { score: number; rewarded: boolean; correctCount?: number; shareRewarded?: boolean }>>({});
    const [owned, setOwned] = useState<Set<string>>(new Set());
    const [duplicates, setDuplicates] = useState<string[]>([]);
    const [packs, setPacks] = useState(2);
    const [connectOpen, setConnectOpen] = useState(false);
    const [achievements, setAchievements] = useState<string[]>([]);
    const [streak, setStreak] = useState<number>(1);
    const [lastActiveDate, setLastActiveDate] = useState<string>('');
    const [predictions, setPredictions] = useState<Record<string, any>>({});
    const [officialResultsRaw, setOfficialResultsRaw] = useState<Record<string, any>>({});
    const [gameStatisticsRaw, setGameStatisticsRaw] = useState<Record<string, any>>({});
    const [firestoreQuotaExceeded, setFirestoreQuotaExceeded] = useState<boolean>(false);

    const officialResults = useMemo(() => {
        const merged: Record<string, any> = {};
        for (const [key, value] of Object.entries(officialResultsRaw)) {
            merged[key] = { ...merged[key], ...value as any };
        }
        for (const [key, value] of Object.entries(gameStatisticsRaw)) {
            merged[key] = { ...merged[key], ...value as any };
        }
        return merged;
    }, [officialResultsRaw, gameStatisticsRaw]);
    const [dailyCheckinBonus, setDailyCheckinBonus] = useState<number | null>(null);
    const [recentUnlockedAchievements, setRecentUnlockedAchievements] = useState<string[]>([]);
    const [packCards, setPackCards] = useState<Sticker[]>([]);
    const [flipped, setFlipped] = useState<Set<number>>(new Set());
    const [collected, setCollected] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState<string | null>(null);

    // Authentication & Data Sync State
    const [user, setUser] = useState<FirebaseUser | null>(null);
    const loadedUserIdRef = useRef<string | null>(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [isInitialLoaded, setIsInitialLoaded] = useState(false);
    const [isGuest, setIsGuest] = useState(false);
    const [authTab, setAuthTab] = useState<'signin' | 'register'>('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [loadingAction, setLoadingAction] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncSuccess, setSyncSuccess] = useState<boolean | null>(null);
    const [pendingGiftsCount, setPendingGiftsCount] = useState<number>(0);
    const [completedTradesCount, setCompletedTradesCount] = useState<number>(0);
    const [friendsPageDefaultTab, setFriendsPageDefaultTab] = useState<'social' | 'trade'>('social');

    const [notificationPermission, setNotificationPermission] = useState<string>(
        typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
    );

    const triggerDeviceNotification = (title: string, body: string) => {
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            try {
                const notif = new Notification(title, {
                    body,
                    icon: 'https://cdn-icons-png.flaticon.com/512/3616/3616423.png'
                });
                notif.onclick = () => {
                    window.focus();
                };
            } catch (err) {
                console.error("Error showing system notification:", err);
            }
        }
    };

    const handleRequestNotificationPermission = async () => {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            return 'unsupported';
        }
        try {
            const permission = await Notification.requestPermission();
            setNotificationPermission(permission);
            if (permission === 'granted') {
                const welcomeNotif = new Notification("🔔 Device Alerts Activated!", {
                    body: "Standard and real-time swap notifications have been successfully loaded for your device!",
                    icon: 'https://cdn-icons-png.flaticon.com/512/3616/3616423.png'
                });
                welcomeNotif.onclick = () => {
                    window.focus();
                };
            }
            return permission;
        } catch (err) {
            console.error("Failed requesting device alert rights:", err);
            return 'default';
        }
    };

    // Authentication Handlers
    const handleGoogleSignIn = async () => {
        setLoadingAction(true);
        setAuthError(null);
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (e: any) {
            console.error(e);
            let errorMsg = "Failed to sign in with Google Account.";
            if (e.code === "auth/unauthorized-domain" || (e.message && e.message.includes("auth/unauthorized-domain"))) {
                errorMsg = "auth/unauthorized-domain: This hosting domain is not authorized for Firebase Authentication in your project settings.";
            } else if (e.message) {
                errorMsg = e.message;
            }
            setAuthError(errorMsg);
        } finally {
            setLoadingAction(false);
        }
    };

    const handleEmailSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password) {
            setAuthError("Please fill in both Email and Password fields.");
            return;
        }
        setLoadingAction(true);
        setAuthError(null);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (e: any) {
            console.error(e);
            let msg = "Invalid email or password. Please verify your credentials.";
            if (e.code === "auth/invalid-credential" || e.code === "auth/wrong-password" || e.code === "auth/user-not-found") {
                msg = "No account matching these credentials found. Try registering instead.";
            } else if (e.code === "auth/invalid-email") {
                msg = "The format of your email address is invalid.";
            }
            setAuthError(msg);
        } finally {
            setLoadingAction(false);
        }
    };

    const handleEmailSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password || !confirmPassword) {
            setAuthError("All registration fields are mandatory.");
            return;
        }
        if (password !== confirmPassword) {
            setAuthError("Passwords do not match. Please verify.");
            return;
        }
        if (password.length < 6) {
            setAuthError("Password warning: Must be at least 6 characters.");
            return;
        }
        setLoadingAction(true);
        setAuthError(null);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            // Set default World Cup 2026 themed avatar and display name derived from email
            const defaultName = email.split('@')[0];
            await updateProfile(userCredential.user, {
                displayName: defaultName,
                photoURL: 'wc_ball'
            });
        } catch (e: any) {
            console.error(e);
            let msg = "Error: Failed to create collector account.";
            if (e.code === "auth/email-already-in-use") {
                msg = "This email address is already linked to an active account.";
            } else if (e.code === "auth/invalid-email") {
                msg = "The format of your email address is invalid.";
            } else if (e.code === "auth/weak-password") {
                msg = "Password security issue. Please use a stronger password.";
            }
            setAuthError(msg);
        } finally {
            setLoadingAction(false);
        }
    };

    const handleLogOut = async () => {
        try {
            loadedUserIdRef.current = null;
            await signOut(auth);
            setIsGuest(false);
            setAuthTab('signin');
            setOwned(new Set());
            setDuplicates([]);
            setPacks(2);
            setView('cover');
        } catch (e) {
            console.error("Logout execution error: ", e);
        }
    };

    const handleResetProgress = async () => {
        // Clear React states first
        setOwned(new Set());
        setDuplicates([]);
        setPacks(2);
        setPredictions({});

        // Clear offline local storage progression
        localStorage.removeItem('paganini-wc-2026');
        localStorage.removeItem('panini-wc-2026');

        // Clean database if logged in
        if (user) {
            try {
                const docRef = doc(db, 'users', user.uid);
                await setDoc(docRef, {
                    userId: user.uid,
                    email: user.email || '',
                    owned: [],
                    duplicates: [],
                    packs: 2,
                    predictions: {},
                    displayName: user.displayName || user.email?.split('@')[0] || 'Collector',
                    photoURL: user.photoURL || 'wc_ball',
                    updatedAt: new Date().toISOString()
                });
            } catch (e) {
                console.error("Firestore user state wipe failure: ", e);
                handleFirestoreError(e, OperationType.UPDATE, `users/${user.uid}`);
            }
        }

        setView('album');
    };

    // Helper to sync old map-based predictions to top-level collection automatically
    const syncUserPredictionsToCollection = async (userId: string, userPreds: any, userData: any) => {
        if (!userPreds || typeof userPreds !== 'object') return;
        for (const [matchId, pred] of Object.entries(userPreds)) {
            if (pred && (pred as any).submitted) {
                const predictionDocId = `${userId}_${matchId}`;
                const predRef = doc(db, 'predictions', predictionDocId);
                try {
                    const predictionPayload: any = {
                        id: predictionDocId,
                        userId: userId,
                        matchId: matchId,
                        scoreA: Number((pred as any).scoreA),
                        scoreB: Number((pred as any).scoreB),
                        goalsOverUnder: (pred as any).goalsOverUnder || (Number((pred as any).scoreA) + Number((pred as any).scoreB) > 2 ? 'over' : 'under'),
                        corners: Number((pred as any).corners || 0),
                        submitted: typeof (pred as any).submitted === 'boolean' ? (pred as any).submitted : true,
                        resolved: typeof (pred as any).resolved === 'boolean' ? (pred as any).resolved : false,
                        status: (pred as any).status || 'active',
                        displayName: userData.displayName || userData.email?.split('@')[0] || 'Collector',
                        photoURL: userData.photoURL || 'wc_ball',
                        email: userData.email || '',
                        updatedAt: (pred as any).updatedAt || new Date().toISOString()
                    };
                    if ((pred as any).actualScoreA !== undefined && (pred as any).actualScoreA !== null) {
                        predictionPayload.actualScoreA = Number((pred as any).actualScoreA);
                    }
                    if ((pred as any).actualScoreB !== undefined && (pred as any).actualScoreB !== null) {
                        predictionPayload.actualScoreB = Number((pred as any).actualScoreB);
                    }
                    if ((pred as any).actualCorners !== undefined && (pred as any).actualCorners !== null) {
                        predictionPayload.actualCorners = Number((pred as any).actualCorners);
                    }
                    if ((pred as any).packsWon !== undefined && (pred as any).packsWon !== null) {
                        predictionPayload.packsWon = Number((pred as any).packsWon);
                    }
                    await setDoc(predRef, predictionPayload, { merge: true });
                } catch (e) {
                    console.error("Auto sync prediction doc error: ", e);
                }
            }
        }
    };

    // Sync state between Firebase / LocalStorage on mount & user changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                if (loadedUserIdRef.current !== currentUser.uid) {
                    loadedUserIdRef.current = null;
                }
            } else {
                loadedUserIdRef.current = 'guest';
            }
            setUser(currentUser);
            if (currentUser) {
                setIsGuest(false);
                try {
                    const docRef = doc(db, 'users', currentUser.uid);
                    let docSnap;
                    try {
                        docSnap = await getDoc(docRef);
                    } catch (e) {
                        handleFirestoreError(e, OperationType.GET, `users/${currentUser.uid}`);
                        return;
                    }

                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        let loadedDuplicates = data.duplicates || [];
                        let loadedPacks = data.packs ?? 2;
                        let loadedStreak = data.streak ?? 1;
                        let loadedAchievements = data.achievements || [];
                        let needsUpdate = false;
                        const updatePayload: any = {};

                        if (currentUser.email === 'gonzos978@gmail.com' && !data.daviesStickerAwarded) {
                            loadedDuplicates = [...loadedDuplicates, 'CAN_P0'];
                            updatePayload.duplicates = loadedDuplicates;
                            updatePayload.daviesStickerAwarded = true;
                            needsUpdate = true;
                            console.log("Automatically awarded A. Davies (CAN_P0) to gonzos978@gmail.com duplicates list.");
                        }

                        if (currentUser.email === 'gonzos978@gmail.com' && !data.superuserPacksAwarded) {
                            loadedPacks += 20;
                            updatePayload.packs = loadedPacks;
                            updatePayload.superuserPacksAwarded = true;
                            needsUpdate = true;
                            console.log("Automatically granted superuser bonus packs.");
                        }

                        const todayStr = getLocalDateString();
                        const lastActive = data.lastActiveDate;
                        if (lastActive !== todayStr) {
                            loadedPacks += 2;
                            updatePayload.packs = loadedPacks;
                            updatePayload.lastActiveDate = todayStr;

                            let nextStreak = 1;
                            if (lastActive) {
                                const prevDate = new Date(lastActive);
                                const todayDate = new Date(todayStr);
                                const diffTime = Math.abs(todayDate.getTime() - prevDate.getTime());
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                if (diffDays === 1) {
                                    nextStreak = loadedStreak + 1;
                                } else if (diffDays > 1) {
                                    nextStreak = 1;
                                } else {
                                    nextStreak = loadedStreak;
                                }
                            }
                            loadedStreak = nextStreak;
                            updatePayload.streak = loadedStreak;
                            needsUpdate = true;
                            setDailyCheckinBonus(2);
                        }

                        if (needsUpdate) {
                            try {
                                updatePayload.updatedAt = new Date().toISOString();
                                await setDoc(docRef, updatePayload, { merge: true });
                            } catch (err) {
                                console.error("Failed to apply automatic daily perks:", err);
                            }
                        }

                        setOwned(new Set(data.owned || []));
                        setDuplicates(loadedDuplicates);
                        setPacks(loadedPacks);
                        setConnectOpen(data.connectOpen ?? false);
                        setStreak(loadedStreak);
                        setLastActiveDate(todayStr);
                        setAchievements(loadedAchievements);
                        setPredictions(data.predictions || {});
                        setQuizHistory(data.quizHistory || {});
                        if (data.predictions) {
                            syncUserPredictionsToCollection(currentUser.uid, data.predictions, data);
                        }
                    } else {
                        // New cloud account: DO NOT restore from localStorage, default to 4 packs (2 standard + 2 login)
                        let initialOwned = new Set<string>();
                        let initialDuplicates: string[] = [];
                        let initialPacks = currentUser.email === 'gonzos978@gmail.com' ? 24 : 4;
                        let isDaviesAwarded = false;

                        if (currentUser.email === 'gonzos978@gmail.com') {
                            initialDuplicates.push('CAN_P0');
                            isDaviesAwarded = true;
                        }

                        const todayStr = getLocalDateString();
                        try {
                            await setDoc(docRef, {
                                userId: currentUser.uid,
                                email: currentUser.email || '',
                                owned: Array.from(initialOwned),
                                duplicates: initialDuplicates,
                                packs: initialPacks,
                                superuserPacksAwarded: currentUser.email === 'gonzos978@gmail.com',
                                daviesStickerAwarded: isDaviesAwarded,
                                displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Collector',
                                photoURL: currentUser.photoURL || 'wc_ball',
                                lastActiveDate: todayStr,
                                streak: 1,
                                achievements: [],
                                updatedAt: new Date().toISOString()
                            });
                        } catch (e) {
                            handleFirestoreError(e, OperationType.CREATE, `users/${currentUser.uid}`);
                            return;
                        }
                        setOwned(initialOwned);
                        setDuplicates(initialDuplicates);
                        setPacks(initialPacks);
                        setStreak(1);
                        setLastActiveDate(todayStr);
                        setAchievements([]);
                        setDailyCheckinBonus(2);
                    }
                    loadedUserIdRef.current = currentUser.uid;
                    setView('album');
                } catch (e) {
                    console.error("Firebase database coordination failure: ", e);
                }
            } else {
                // Guest mode / offline fallback: load offline local progression
                const saved = localStorage.getItem('paganini-wc-2026') || localStorage.getItem('panini-wc-2026');
                if (saved) {
                    try {
                        const data = JSON.parse(saved);
                        setOwned(new Set(data.owned || []));
                        setDuplicates(data.duplicates || []);
                        let loadedPacks = typeof data.packs === 'number' ? data.packs : 2;
                        let loadedStreak = data.streak ?? 1;
                        let loadedAchievements = data.achievements || [];
                        const todayStr = getLocalDateString();
                        const lastActive = data.lastActiveDate;

                        if (lastActive !== todayStr) {
                            loadedPacks += 2;
                            let nextStreak = 1;
                            if (lastActive) {
                                const prevDate = new Date(lastActive);
                                const todayDate = new Date(todayStr);
                                const diffTime = Math.abs(todayDate.getTime() - prevDate.getTime());
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                if (diffDays === 1) {
                                    nextStreak = loadedStreak + 1;
                                } else if (diffDays > 1) {
                                    nextStreak = 1;
                                } else {
                                    nextStreak = loadedStreak;
                                }
                            }
                            loadedStreak = nextStreak;
                            setDailyCheckinBonus(2);
                            localStorage.setItem('paganini-wc-2026', JSON.stringify({
                                owned: data.owned || [],
                                duplicates: data.duplicates || [],
                                packs: loadedPacks,
                                lastActiveDate: todayStr,
                                streak: loadedStreak,
                                achievements: loadedAchievements
                            }));
                        }
                        setPacks(loadedPacks);
                        setStreak(loadedStreak);
                        setLastActiveDate(todayStr);
                        setAchievements(loadedAchievements);
                        setPredictions(data.predictions || {});
                        setQuizHistory(data.quizHistory || {});
                    } catch (e) {
                        console.error('Failed to parse offline local storage progression:', e);
                    }
                } else {
                    const todayStr = getLocalDateString();
                    setOwned(new Set());
                    setDuplicates([]);
                    setPacks(4); // Give 2 standard + 2 login start packs!
                    setStreak(1);
                    setLastActiveDate(todayStr);
                    setAchievements([]);
                    setDailyCheckinBonus(2);
                    localStorage.setItem('paganini-wc-2026', JSON.stringify({
                        owned: [],
                        duplicates: [],
                        packs: 4,
                        lastActiveDate: todayStr,
                        streak: 1,
                        achievements: []
                    }));
                }
                loadedUserIdRef.current = 'guest';
            }
            setLoadingAuth(false);
            setIsInitialLoaded(true);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        registerQuotaExceededListener((isExceeded) => {
            setFirestoreQuotaExceeded(isExceeded);
        });
    }, []);

    // Realtime user document updates listener to prevent local drafts from wiping out admin verifications
    useEffect(() => {
        if (!user) return;
        const docRef = doc(db, 'users', user.uid);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists() && loadedUserIdRef.current === user.uid) {
                const data = docSnap.data();

                // Sync major stats in real-time, especially when updated by admin/superuser
                setPredictions(prev => {
                    if (JSON.stringify(prev) !== JSON.stringify(data.predictions || {})) {
                        return data.predictions || {};
                    }
                    return prev;
                });

                if (data.packs !== undefined) {
                    setPacks(prev => prev !== data.packs ? data.packs : prev);
                }

                if (data.streak !== undefined) {
                    setStreak(prev => prev !== data.streak ? data.streak : prev);
                }

                if (data.lastActiveDate !== undefined) {
                    setLastActiveDate(prev => prev !== data.lastActiveDate ? data.lastActiveDate : prev);
                }

                if (data.owned !== undefined) {
                    setOwned(prev => {
                        const nextOwned = new Set<string>(data.owned || []);
                        if (prev.size !== nextOwned.size || ![...prev].every(x => nextOwned.has(x))) {
                            return nextOwned;
                        }
                        return prev;
                    });
                }

                if (data.duplicates !== undefined) {
                    setDuplicates(prev => {
                        if (JSON.stringify(prev) !== JSON.stringify(data.duplicates)) {
                            return data.duplicates || [];
                        }
                        return prev;
                    });
                }

                if (data.achievements !== undefined) {
                    setAchievements(prev => {
                        if (JSON.stringify(prev) !== JSON.stringify(data.achievements)) {
                            return data.achievements || [];
                        }
                        return prev;
                    });
                }
            }
        }, (error) => {
            console.error("Realtime user document listener failed: ", error);
            if (isFirestoreQuotaError(error)) {
                setFirestoreQuotaExceeded(true);
            }
        });

        return () => unsubscribe();
    }, [user]);

    // Real-time listener for official_results and gamestatistics collections globally
    useEffect(() => {
        const q1 = collection(db, 'official_results');
        const q2 = collection(db, 'gamestatistics');

        const unsub1 = onSnapshot(q1, (snapshot) => {
            const results: Record<string, any> = {};
            snapshot.forEach((doc) => {
                results[doc.id] = doc.data();
            });
            setOfficialResultsRaw(results);
        }, (error) => {
            console.error("Failed to sync global official results: ", error);
            if (isFirestoreQuotaError(error)) {
                setFirestoreQuotaExceeded(true);
            }
        });

        const unsub2 = onSnapshot(q2, (snapshot) => {
            const stats: Record<string, any> = {};
            snapshot.forEach((doc) => {
                stats[doc.id] = doc.data();
            });
            setGameStatisticsRaw(stats);
        }, (error) => {
            console.error("Failed to sync global gamestatistics: ", error);
            if (isFirestoreQuotaError(error)) {
                setFirestoreQuotaExceeded(true);
            }
        });

        return () => {
            unsub1();
            unsub2();
        };
    }, []);

    // Automatically reconcile user predictions against official results
    useEffect(() => {
        if (!isInitialLoaded) return;

        let changed = false;
        const nextPredictions = { ...predictions } as Record<string, any>;
        let additionalPacksGranted = 0;

        for (const [matchId, officialVal] of Object.entries(officialResults)) {
            const official = officialVal as any;
            if (!official || !official.resolved) continue;

            const pred = nextPredictions[matchId] as any;
            if (pred && !pred.resolved) {
                // We have a submitted prediction that is not yet resolved!
                const scoreA = Number(official.actualScoreA);
                const scoreB = Number(official.actualScoreB);
                const corners = Number(official.actualCorners);
                const goalsOverUnder = (scoreA + scoreB) > 2.5 ? 'over' : 'under';

                // Scoring rules: exact scoreline 5 points, goals O/U 2 points, corners 3 points
                const isScoreCorrect = (Number(pred.scoreA) === scoreA && Number(pred.scoreB) === scoreB);
                const isGoalsCorrect = (pred.goalsOverUnder === goalsOverUnder);
                const isCornersCorrect = Math.abs(Number(pred.corners || 0) - corners) <= 1;

                let pointsWon = 0;
                if (isScoreCorrect) pointsWon += 5;
                if (isGoalsCorrect) pointsWon += 2;
                if (isCornersCorrect) pointsWon += 3;

                let packsWon = 0;
                if (isScoreCorrect) packsWon += 3;
                if (isCornersCorrect) packsWon += 2;
                if (isGoalsCorrect) packsWon += 1;

                nextPredictions[matchId] = {
                    ...pred,
                    resolved: true,
                    status: 'finished',
                    actualScoreA: scoreA,
                    actualScoreB: scoreB,
                    actualCorners: corners,
                    pointsWon,
                    packsWon,
                    updatedAt: new Date().toISOString()
                };
                additionalPacksGranted += packsWon;
                changed = true;

                // If logged in, also update individual prediction document inpredictions collection
                if (user) {
                    const predictionDocId = `${user.uid}_${matchId}`;
                    const predRef = doc(db, 'predictions', predictionDocId);
                    setDoc(predRef, {
                        resolved: true,
                        status: 'finished',
                        actualScoreA: scoreA,
                        actualScoreB: scoreB,
                        actualCorners: corners,
                        pointsWon,
                        packsWon,
                        updatedAt: new Date().toISOString()
                    }, { merge: true }).catch(err => {
                        console.error("Failed to sync auto-resolved prediction to predictions collection:", err);
                    });
                }
            }
        }

        // Checking for reverted predictions
        for (const [matchId, predVal] of Object.entries(predictions)) {
            const pred = predVal as any;
            if (pred && pred.resolved) {
                const official = officialResults[matchId] as any;
                if (!official || !official.resolved) {
                    // Revert prediction back to unresolved!
                    const oldPacksWon = Number(pred.packsWon || 0);
                    nextPredictions[matchId] = {
                        ...pred,
                        resolved: false,
                        status: 'active',
                        actualScoreA: null,
                        actualScoreB: null,
                        actualCorners: null,
                        pointsWon: 0,
                        packsWon: 0,
                        updatedAt: new Date().toISOString()
                    };
                    additionalPacksGranted -= oldPacksWon;
                    changed = true;

                    // If logged in, also update individual prediction document inpredictions collection
                    if (user) {
                        const predictionDocId = `${user.uid}_${matchId}`;
                        const predRef = doc(db, 'predictions', predictionDocId);
                        setDoc(predRef, {
                            resolved: false,
                            status: 'active',
                            actualScoreA: null,
                            actualScoreB: null,
                            actualCorners: null,
                            pointsWon: 0,
                            packsWon: 0,
                            updatedAt: new Date().toISOString()
                        }, { merge: true }).catch(err => {
                            console.error("Failed to sync auto-reverted prediction to predictions collection:", err);
                        });
                    }
                }
            }
        }

        if (changed) {
            setPredictions(nextPredictions);
            if (additionalPacksGranted !== 0) {
                setPacks(prev => Math.max(0, prev + additionalPacksGranted));
            }
        }
    }, [officialResults, predictions, isInitialLoaded, user]);

    const predictionPoints = useMemo(() => {
        return Object.values(predictions).reduce((sum, pred: any) => {
            if (pred && pred.resolved) {
                return sum + (pred.pointsWon ?? pred.packsWon ?? 0);
            }
            return sum;
        }, 0);
    }, [predictions]);

    const handleSaveQuizResult = async (date: string, score: number, rewarded: boolean, correctCount?: number) => {
        const currentRecord = quizHistory[date] || {};
        const nextHistory = {
            ...quizHistory,
            [date]: {
                ...currentRecord,
                score,
                rewarded,
                correctCount: correctCount ?? (score <= 10 ? score : undefined)
            }
        };
        setQuizHistory(nextHistory);

        if (user) {
            try {
                const docRef = doc(db, 'users', user.uid);
                await setDoc(docRef, {
                    quizHistory: nextHistory,
                    updatedAt: new Date().toISOString()
                }, { merge: true });

                // Save result on firebase collection quizleaderboard
                const entryId = `${user.uid}_${date}`;
                const leaderboardRef = doc(db, 'quizleaderboard', entryId);
                await setDoc(leaderboardRef, {
                    id: entryId,
                    userId: user.uid,
                    email: user.email || '',
                    displayName: user.displayName || user.email?.split('@')[0] || 'Collector',
                    photoURL: user.photoURL || 'wc_ball',
                    date,
                    score,
                    correctCount: correctCount ?? (score <= 10 ? score : undefined),
                    accuracy: (correctCount ?? (score <= 10 ? score : 8)) * 10,
                    createdAt: new Date().toISOString()
                });
            } catch (e) {
                console.error("Failed to save quiz score directly:", e);
                handleFirestoreError(e, OperationType.UPDATE, `quizleaderboard/${user.uid}_${date}`);
            }
        }
    };

    const handleResetQuiz = async (date: string) => {
        const nextHistory = { ...quizHistory };
        delete nextHistory[date];
        setQuizHistory(nextHistory);

        if (user) {
            try {
                const docRef = doc(db, 'users', user.uid);
                await setDoc(docRef, {
                    quizHistory: nextHistory,
                    updatedAt: new Date().toISOString()
                }, { merge: true });

                // Also delete from firebase quizleaderboard entry
                const entryId = `${user.uid}_${date}`;
                await deleteDoc(doc(db, 'quizleaderboard', entryId));
                console.log("Successfully reset quiz history for today");
            } catch (e) {
                console.error("Failed to reset quiz history in Firestore:", e);
                handleFirestoreError(e, OperationType.DELETE, `quizleaderboard/${user.uid}_${date}`);
            }
        }
    };

    const handleClaimShareReward = async (date: string) => {
        if (quizHistory[date]?.shareRewarded) return;

        const currentRecord = quizHistory[date] || { score: 0, rewarded: false };
        const nextHistory = {
            ...quizHistory,
            [date]: {
                ...currentRecord,
                shareRewarded: true
            }
        };

        setPacks(prev => {
            const nextPacks = prev + 1;
            if (user) {
                const docRef = doc(db, 'users', user.uid);
                setDoc(docRef, {
                    quizHistory: nextHistory,
                    packs: nextPacks,
                    updatedAt: new Date().toISOString()
                }, { merge: true }).catch(err => {
                    console.error("Failed to save share reward on Firebase:", err);
                });
            }
            return nextPacks;
        });

        setQuizHistory(nextHistory);
    };

    // Sync state changes to current storage container (cloud or local storage)
    useEffect(() => {
        if (!isInitialLoaded) return;

        localStorage.setItem('paganini-wc-2026', JSON.stringify({
            owned: Array.from(owned),
            duplicates,
            packs,
            lastActiveDate,
            streak,
            achievements,
            predictions,
            predictionPoints,
            quizHistory
        }));

        if (user) {
            if (loadedUserIdRef.current !== user.uid) {
                // Skip syncing to cloud because the current user's database copy is NOT yet loaded into the React state
                return;
            }
            const syncToCloud = async () => {
                try {
                    const docRef = doc(db, 'users', user.uid);
                    await setDoc(docRef, {
                        userId: user.uid,
                        email: user.email || '',
                        owned: Array.from(owned),
                        duplicates: duplicates,
                        packs: packs,
                        connectOpen: connectOpen,
                        displayName: user.displayName || user.email?.split('@')[0] || 'Collector',
                        photoURL: user.photoURL || 'wc_ball',
                        lastActiveDate,
                        streak,
                        achievements,
                        predictions,
                        predictionPoints,
                        quizHistory,
                        updatedAt: new Date().toISOString()
                    });
                } catch (e) {
                    console.error("Firestore cloud sync update failed: ", e);
                    handleFirestoreError(e, OperationType.UPDATE, `users/${user.uid}`);
                }
            };

            const timer = setTimeout(() => {
                syncToCloud();
            }, 500); // 500ms debounce to prevent spamming Firestore

            return () => clearTimeout(timer);
        }
    }, [owned, duplicates, packs, connectOpen, user, isInitialLoaded, lastActiveDate, streak, achievements, predictions, predictionPoints, quizHistory]);

    // Real-time automatic achievements validation monitor
    useEffect(() => {
        if (!isInitialLoaded) return;

        // Check custom profile configuration
        const defaultName = user ? (user.email ? user.email.split('@')[0] : '') : '';
        const nameIsCustom = user && user.displayName && user.displayName !== defaultName && user.displayName !== 'Collector';
        const photoIsCustom = user && user.photoURL && user.photoURL !== 'wc_ball';
        const profileCustomized = nameIsCustom || photoIsCustom;

        const unlocked: string[] = [];
        const totalStickersCount = ALL_STICKERS.length;
        const currentOwnedCount = owned.size;
        const pct = totalStickersCount > 0 ? (currentOwnedCount / totalStickersCount) * 100 : 0;

        // 1. First Unboxing
        if (currentOwnedCount > 0) {
            unlocked.push("first_pack");
        }

        // 2. Legendary Sticker Award
        const hasLegendary = Array.from(owned).some((id: any) => STICKER_MAP[id]?.rarity === 'legendary');
        if (hasLegendary) {
            unlocked.push("lucky_draw");
        }

        // 3. Double trouble duplicate
        if (duplicates.length > 0) {
            unlocked.push("double_trouble");
        }

        // 4. Progress based
        if (pct >= 10) {
            unlocked.push("novice_scout");
        }
        if (pct >= 35) {
            unlocked.push("pro_collector");
        }
        if (pct >= 70) {
            unlocked.push("elite_curator");
        }

        // 5. Active daily streak
        if (streak >= 3) {
            unlocked.push("active_streak_3");
        }

        // 6. Customization
        if (profileCustomized) {
            unlocked.push("profile_pioneer");
        }

        // 7. Beacon discoverability
        if (connectOpen) {
            unlocked.push("beacon_active");
        }

        const newUnlocks = unlocked.filter(id => !achievements.includes(id));
        if (newUnlocks.length > 0) {
            let rewardSum = 0;
            newUnlocks.forEach(achId => {
                const ach = ACHIEVEMENTS.find(a => a.id === achId);
                if (ach) {
                    rewardSum += ach.rewardPacks;
                    triggerDeviceNotification(
                        `🏆 Achievement Unlocked: ${ach.title}!`,
                        `"${ach.description}" Reward: +${ach.rewardPacks} Pack(s)`
                    );
                }
            });

            // Update state triggers
            setAchievements(prev => [...prev, ...newUnlocks]);
            setPacks(p => p + rewardSum);
            setRecentUnlockedAchievements(prev => [...prev, ...newUnlocks]);
        }
    }, [owned, duplicates, streak, connectOpen, user, isInitialLoaded, achievements]);

    // Listen to pending gifts count in real time
    const initialGiftsLoaded = useRef(false);
    useEffect(() => {
        if (!user) {
            setPendingGiftsCount(0);
            initialGiftsLoaded.current = false;
            return;
        }
        const q = query(
            collection(db, 'gifts'),
            where('receiverId', '==', user.uid),
            where('status', '==', 'pending')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPendingGiftsCount(snapshot.size);

            if (!initialGiftsLoaded.current) {
                initialGiftsLoaded.current = true;
            } else {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added') {
                        const data = change.doc.data();
                        const sender = data.senderName || 'A friend';
                        const stickerName = STICKER_MAP[data.stickerId]?.name || 'a copy';
                        triggerDeviceNotification(
                            "🎁 Gift Received!",
                            `${sender} sent you ${stickerName}! Open your profile mailbox to claim it.`
                        );
                    }
                });
            }
        }, (error) => {
            console.error("Gifts list subscription failed: ", error);
            if (isFirestoreQuotaError(error)) {
                setFirestoreQuotaExceeded(true);
            }
        });
        return () => unsubscribe();
    }, [user]);

    // Listen to completed trades count in real time (user as trade creator)
    const initialTradesLoaded = useRef(false);
    useEffect(() => {
        if (!user) {
            setCompletedTradesCount(0);
            initialTradesLoaded.current = false;
            return;
        }
        const q = query(
            collection(db, 'trades'),
            where('userId', '==', user.uid),
            where('status', '==', 'completed')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCompletedTradesCount(snapshot.size);

            if (!initialTradesLoaded.current) {
                initialTradesLoaded.current = true;
            } else {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added' || change.type === 'modified') {
                        const data = change.doc.data();
                        if (data.status === 'completed') {
                            const partner = data.partnerName || 'Another collector';
                            const wantedName = STICKER_MAP[data.wantedStickerId]?.name || 'a sticker';
                            triggerDeviceNotification(
                                "🤝 Sticker Exchanged!",
                                `${partner} accepted your offer and swapped you ${wantedName}! Claim it now on your Friends trade board.`
                            );
                        }
                    }
                });
            }
        }, (error) => {
            console.error("Trades subscription failed: ", error);
            if (isFirestoreQuotaError(error)) {
                setFirestoreQuotaExceeded(true);
            }
        });
        return () => unsubscribe();
    }, [user]);

    const randomPack = () => {
        const rare = ALL_STICKERS.filter(s => s.rarity !== 'common');
        const seen = new Set<string>();
        const pack: Sticker[] = [];

        // Ensure one rare/legendary
        const guaranteed = rare[Math.floor(Math.random() * rare.length)];
        pack.push(guaranteed);
        seen.add(guaranteed.id);

        while (pack.length < 5) {
            const s = ALL_STICKERS[Math.floor(Math.random() * ALL_STICKERS.length)];
            if (!seen.has(s.id)) {
                pack.push(s);
                seen.add(s.id);
            }
        }
        return pack.sort(() => Math.random() - 0.5);
    };

    const doOpen = () => {
        if (packs <= 0) return;
        setPacks(p => p - 1);
        setPackCards(randomPack());
        setFlipped(new Set());
        setCollected(false);
        setSyncSuccess(null);
        setView('pack');
    };

    const doCollect = async () => {
        const nextOwned = new Set(owned);
        const addedDuplicates: string[] = [];

        packCards.forEach(s => {
            if (nextOwned.has(s.id)) {
                addedDuplicates.push(s.id);
            } else {
                nextOwned.add(s.id);
            }
        });

        const nextDuplicates = [...duplicates, ...addedDuplicates];
        setOwned(nextOwned);
        setDuplicates(nextDuplicates);
        setCollected(true);
        setFlipped(new Set([0, 1, 2, 3, 4]));

        if (user) {
            setIsSyncing(true);
            setSyncSuccess(null);
            try {
                const docRef = doc(db, 'users', user.uid);
                await setDoc(docRef, {
                    userId: user.uid,
                    email: user.email || '',
                    owned: Array.from(nextOwned),
                    duplicates: nextDuplicates,
                    packs: packs, // Packs has already been decremented by 1 in doOpen
                    displayName: user.displayName || user.email?.split('@')[0] || 'Collector',
                    photoURL: user.photoURL || 'wc_ball',
                    updatedAt: new Date().toISOString()
                });
                setSyncSuccess(true);
            } catch (error) {
                console.error("Failed to sync to Firebase database on manual claim: ", error);
                setSyncSuccess(false);
                handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
            } finally {
                setIsSyncing(false);
            }
        } else {
            localStorage.setItem('paganini-wc-2026', JSON.stringify({
                owned: Array.from(nextOwned),
                duplicates: nextDuplicates,
                packs: packs
            }));
            setSyncSuccess(true);
        }
    };

    const pct = Math.round((owned.size / ALL_STICKERS.length) * 100);

    // If initial connection lookup is loading, show loading screen
    if (loadingAuth) {
        return (
            <div className="min-h-screen bg-[#0f172a] text-slate-300 font-sans flex flex-col items-center justify-center p-6 text-center select-none relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
                    <div className="absolute top-1/4 left-1/4 w-[50%] h-[50%] rounded-full bg-blue-500 blur-[150px]"></div>
                </div>
                <div className="relative z-10 flex flex-col items-center">
                    <div className="w-16 h-16 border-4 border-slate-800 border-t-blue-500 rounded-full animate-spin mb-6" />
                    <h1 className="font-mono text-xs font-bold tracking-[0.4em] text-slate-400 uppercase mb-2">Connecting_Service</h1>
                    <p className="font-mono text-[9px] text-blue-500 uppercase tracking-widest animate-pulse">Establishing secure handshake...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0f172a] text-slate-300 font-sans selection:bg-blue-600 selection:text-white">
            {view !== 'cover' && (
                <Navigation
                    view={view}
                    setView={setView}
                    packs={packs}
                    setPacks={setPacks}
                    doOpen={doOpen}
                    owned={owned}
                    user={user}
                    onLogOut={handleLogOut}
                    giftCount={pendingGiftsCount}
                    completedTradesCount={completedTradesCount}
                    lang={lang}
                    setLang={setLang}
                />
            )}

            {/* Firestore Quota Exceeded Friendly Fallback Notification Banner */}
            <AnimatePresence>
                {firestoreQuotaExceeded && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-gradient-to-r from-amber-500 via-orange-600 to-red-650 text-white text-center flex items-center justify-between gap-3 text-xs font-mono font-bold tracking-wide uppercase transition-all shadow-lg shadow-orange-500/20 sticky top-14 z-[99] border-b border-orange-500/30 overflow-hidden py-3 px-5"
                    >
                        <div className="flex items-center gap-2 mx-auto justify-center select-none text-center">
                            <span className="text-sm">☁️</span>
                            <span>CLOUD SERVICES ARE UNDER HEAVY TRAFFIC LOAD. PROGRESS SECURED LOCALLY ON YOUR DEVICE AS OFFLINE PLAY IS FULLY ACTIVE!</span>
                        </div>
                        <button
                            onClick={() => setFirestoreQuotaExceeded(false)}
                            className="text-white hover:text-slate-200 transition-colors bg-slate-950/40 hover:bg-slate-950/80 rounded-full p-1 border border-white/10 transition-all cursor-pointer font-bold w-6 h-6 flex items-center justify-center text-xs shrink-0"
                            title="Acknowledge local offline operation mode"
                        >
                            ✕
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating Pending Gift Notification Banner */}
            <AnimatePresence>
                {view !== 'profile' && user && pendingGiftsCount > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-gradient-to-r from-amber-600 to-yellow-600 text-slate-950 text-center flex items-center justify-center gap-2 text-xs font-mono font-bold tracking-wide uppercase transition-all shadow-lg shadow-amber-600/20 sticky top-14 z-40 border-b border-amber-500/30 overflow-hidden cursor-pointer py-2.5 px-4"
                        onClick={() => setView('profile')}
                    >
                        <span className="animate-bounce">🎁</span>
                        <span>You received {pendingGiftsCount} sticker gift{pendingGiftsCount > 1 ? 's' : ''}! Click here to open your profile and claim them!</span>
                        <span className="underline ml-1 cursor-pointer font-black text-[10px] bg-slate-950 text-amber-550 px-2 py-0.5 rounded">CLAIM NOW →</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating Sticker Exchanged Notification Banner */}
            <AnimatePresence>
                {view !== 'friends' && user && completedTradesCount > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-center flex items-center justify-center gap-2 text-xs font-mono font-bold tracking-wide uppercase transition-all shadow-lg shadow-emerald-600/20 sticky top-14 z-40 border-b border-emerald-500/30 overflow-hidden cursor-pointer py-2.5 px-4"
                        onClick={() => {
                            setFriendsPageDefaultTab('trade');
                            setView('friends');
                        }}
                    >
                        <span className="animate-bounce text-base">🤝</span>
                        <span>Sticker Exchanged! You have {completedTradesCount} claimable trade{completedTradesCount > 1 ? 's' : ''} on your friends exchange board!</span>
                        <span className="underline ml-1 cursor-pointer font-black text-[10px] bg-slate-950 text-emerald-400 px-2 py-0.5 rounded">COLLECT NOW ➔</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Daily Check-in Bonus Notification Banner */}
            <AnimatePresence>
                {dailyCheckinBonus && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-gradient-to-r from-blue-700 via-indigo-600 to-violet-700 text-white text-center flex items-center justify-between gap-2 text-xs font-mono font-bold tracking-wide uppercase transition-all shadow-lg shadow-blue-600/30 sticky top-14 z-50 border-b border-indigo-500/30 overflow-hidden py-3 px-4"
                    >
                        <div className="flex items-center gap-2 mx-auto justify-center select-none">
                            <span className="animate-spin text-base">✨</span>
                            <span>DAILY CHECK-IN SYSTEM ACTIVE: +{dailyCheckinBonus} Free Sticker Packs Credited! (Streak: {streak} Day{streak > 1 ? 's' : ''} 🔥)</span>
                        </div>
                        <button
                            onClick={() => setDailyCheckinBonus(null)}
                            className="text-white bg-slate-950/40 hover:bg-slate-950/80 rounded-full p-1 border border-white/20 transition-all cursor-pointer font-bold w-6 h-6 flex items-center justify-center text-xs"
                        >
                            ✕
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Achievements Unlocked Notification Banner List */}
            <div className="fixed bottom-6 right-6 z-[120] flex flex-col gap-3 pointer-events-none">
                <AnimatePresence>
                    {recentUnlockedAchievements.map((achId) => {
                        const ach = ACHIEVEMENTS.find(a => a.id === achId);
                        if (!ach) return null;
                        return (
                            <motion.div
                                key={ach.id}
                                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                                className="bg-gradient-to-br from-slate-900 to-[#1e293b] border-2 border-amber-500 rounded-xl p-4 shadow-[0_4px_30px_rgba(245,158,11,0.25)] flex items-start gap-3.5 max-w-sm pointer-events-auto"
                            >
                                <div className="text-3xl flex-shrink-0 bg-amber-500/10 border border-amber-500/20 w-12 h-12 rounded-lg flex items-center justify-center select-none">
                                    🏆
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                    <div className="flex items-center justify-between">
                                        <h5 className="text-[10px] font-mono font-bold tracking-widest text-amber-500 uppercase leading-none">Milestone Reached!</h5>
                                        <button
                                            onClick={() => setRecentUnlockedAchievements(prev => prev.filter(uid => uid !== achId))}
                                            className="text-slate-500 hover:text-white font-mono text-[9px] cursor-pointer"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <h4 className="text-sm font-bold text-white mt-1 uppercase tracking-tight">{ach.title}</h4>
                                    <p className="text-[10px] text-slate-400 font-sans mt-0.5 leading-normal">{ach.description}</p>
                                    <div className="mt-2.5 flex items-center justify-between bg-slate-950/50 border border-slate-800 px-2 py-1 rounded">
                                        <span className="text-[8px] font-mono text-slate-500">REWARD_CREDITED</span>
                                        <span className="text-[8px] font-mono font-bold text-emerald-400">+{ach.rewardPacks} PACKS</span>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>

            <AnimatePresence mode="wait">
                {view === 'cover' && (
                    <motion.div
                        key="cover"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-[#0f172a] relative overflow-hidden select-none"
                    >
                        {/* Geometric Accents */}
                        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-500 blur-[120px]"></div>
                            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-emerald-500 blur-[120px]"></div>
                        </div>

                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            className="mb-4 p-5 rounded-lg bg-[#1e293b] border border-slate-700 text-blue-500 shadow-2xl relative z-10"
                        >
                            <Trophy size={54} />
                        </motion.div>

                        <h1 className="font-impact text-4xl md:text-6xl tracking-tighter text-white leading-none mb-1 relative z-10 uppercase">
                            PAGANINI <span className="text-blue-500">2026</span>
                        </h1>
                        <p className="text-[9px] font-mono tracking-[0.4em] text-slate-500 font-bold mb-8 uppercase relative z-10">
                            STICKER COLLECTION PLATFORM
                        </p>

                        {/* If signed out and NOT playing as guest: Show Auth Form Panel */}
                        {!user && !isGuest ? (
                            <motion.div
                                key="auth"
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="w-full max-w-sm p-6 bg-[#1e293b]/90 border border-slate-700/80 rounded shadow-2xl relative z-20 backdrop-blur-md text-left"
                            >
                                {/* Tabs */}
                                <div className="flex border-b border-slate-700 mb-5">
                                    <button
                                        onClick={() => { setAuthTab('signin'); setAuthError(null); }}
                                        className={`flex-1 pb-3 text-xs font-mono font-bold uppercase tracking-wider transition-colors ${authTab === 'signin' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-slate-500 hover:text-slate-300'}`}
                                    >
                                        Sign In
                                    </button>
                                    <button
                                        onClick={() => { setAuthTab('register'); setAuthError(null); }}
                                        className={`flex-1 pb-3 text-xs font-mono font-bold uppercase tracking-wider transition-colors ${authTab === 'register' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-500 hover:text-slate-300'}`}
                                    >
                                        Register
                                    </button>
                                </div>

                                {/* Error Banner */}
                                {authError && (
                                    <div className="mb-4 p-3 bg-red-950/40 border border-red-800/80 rounded flex flex-col gap-2.5 text-xs text-red-200">
                                        <div className="flex items-start gap-2.5">
                                            <AlertCircle size={15} className="text-red-400 shrink-0 mt-0.5 animate-pulse" />
                                            <span className="font-mono text-[10px] uppercase tracking-wide leading-tight">
                        {authError.includes('auth/unauthorized-domain')
                            ? "Firebase Auth Error: Domain is not allowed."
                            : authError}
                      </span>
                                        </div>

                                        {authError.toLowerCase().includes('unauthorized-domain') && (
                                            <div className="mt-2 pt-2 border-t border-red-900/40 text-[10px] font-mono leading-relaxed text-slate-300">
                                                <p className="text-yellow-400 font-bold uppercase mb-1">⚠️ Firebase Console Step Required:</p>
                                                <p className="mb-2 text-slate-400">Your current development/shared app URL must be added to your Authorized Domains list in the Firebase console.</p>

                                                <div className="bg-[#0f172a] p-2.5 rounded border border-slate-700/60 mb-3">
                                                    <span className="text-slate-500 text-[8px] uppercase block mb-1">Domain to authorized list:</span>
                                                    <code
                                                        onClick={(e) => {
                                                            navigator.clipboard.writeText(window.location.hostname);
                                                            const t = e.currentTarget;
                                                            const prev = t.innerText;
                                                            t.innerText = "COPIED TO CLIPBOARD!";
                                                            setTimeout(() => t.innerText = prev, 1500);
                                                        }}
                                                        className="text-emerald-400 block font-bold text-xs select-all cursor-pointer bg-slate-900/50 p-1 rounded border border-emerald-950 hover:bg-slate-900 transition-colors"
                                                        title="Click to copy domain"
                                                    >
                                                        {window.location.hostname}
                                                    </code>
                                                </div>

                                                <div className="space-y-1 text-slate-400 text-[10px]">
                                                    <p className="font-bold text-slate-200 uppercase text-[9px]">How to add it:</p>
                                                    <ol className="list-decimal list-inside space-y-1">
                                                        <li>
                                                            Open your{" "}
                                                            <a
                                                                href="https://console.firebase.google.com/project/paganini2026-ce3e7/authentication/settings"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-400 underline hover:text-blue-300 font-bold"
                                                            >
                                                                Firebase settings console
                                                            </a>
                                                        </li>
                                                        <li>Scroll to the <span className="text-slate-200 font-semibold">Authorized domains</span> section.</li>
                                                        <li>Click <span className="text-slate-200 font-semibold">Add domain</span>.</li>
                                                        <li>Paste the copied domain above and click <span className="text-slate-200 font-semibold">Add</span>.</li>
                                                    </ol>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Forms */}
                                <form onSubmit={authTab === 'signin' ? handleEmailSignIn : handleEmailSignUp} className="space-y-4">
                                    <div>
                                        <label className="block text-[8px] font-mono uppercase tracking-widest text-slate-500 mb-1">Email address</label>
                                        <div className="relative">
                                            <Mail size={13} className="absolute left-3 top-3 text-slate-500" />
                                            <input
                                                type="email"
                                                required
                                                placeholder="collector@example.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full pl-9 pr-4 py-2 bg-[#0f172a] border border-slate-700 rounded text-[11px] text-white focus:outline-none focus:border-blue-500 font-mono shadow-inner"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[8px] font-mono uppercase tracking-widest text-slate-500 mb-1">Password</label>
                                        <div className="relative">
                                            <Lock size={13} className="absolute left-3 top-3 text-slate-500" />
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                required
                                                placeholder="••••••••"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-9 pr-9 py-2 bg-[#0f172a] border border-slate-700 rounded text-[11px] text-white focus:outline-none focus:border-blue-500 font-mono shadow-inner"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-2 text-slate-500 hover:text-slate-300 transition-colors"
                                            >
                                                {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                                            </button>
                                        </div>
                                    </div>

                                    {authTab === 'register' && (
                                        <div>
                                            <label className="block text-[8px] font-mono uppercase tracking-widest text-slate-500 mb-1">Confirm Password</label>
                                            <div className="relative">
                                                <Lock size={13} className="absolute left-3 top-3 text-slate-500" />
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    required
                                                    placeholder="••••••••"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    className="w-full pl-9 pr-4 py-2 bg-[#0f172a] border border-slate-700 rounded text-[11px] text-white focus:outline-none focus:border-blue-500 font-mono shadow-inner"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loadingAction}
                                        className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-mono font-bold uppercase tracking-wider rounded transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-blue-500/10 disabled:opacity-50"
                                    >
                                        {loadingAction ? (
                                            <RefreshCw size={13} className="animate-spin" />
                                        ) : authTab === 'signin' ? (
                                            <>
                                                <LogIn size={13} />
                                                Sign In Account
                                            </>
                                        ) : (
                                            <>
                                                <UserPlus size={13} />
                                                Register & Setup
                                            </>
                                        )}
                                    </button>
                                </form>

                                {/* Divider */}
                                <div className="relative my-4.5">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-slate-800"></div>
                                    </div>
                                    <div className="relative flex justify-center text-[8px] font-mono uppercase tracking-widest text-slate-600">
                                        <span className="bg-[#1e293b] px-2">OR CONNECT VIA</span>
                                    </div>
                                </div>

                                {/* Google Login Button */}
                                <button
                                    type="button"
                                    disabled={loadingAction}
                                    onClick={handleGoogleSignIn}
                                    className="w-full py-2 bg-[#0f172a] border border-slate-700 text-slate-300 hover:text-white text-[10px] font-mono font-bold uppercase tracking-wider rounded transition-all flex items-center justify-center gap-2.5 cursor-pointer hover:border-slate-500 disabled:opacity-50 shadow-sm"
                                >
                                    <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24">
                                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fillRule="evenodd" />
                                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fillRule="evenodd" />
                                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fillRule="evenodd" />
                                    </svg>
                                    Google Account Sync
                                </button>

                                {/* Switch to Guest bypass option */}
                                <button
                                    onClick={() => setIsGuest(true)}
                                    className="w-full mt-3 py-1.5 text-center text-slate-500 hover:text-slate-400 font-mono text-[9px] uppercase tracking-widest border border-dashed border-slate-700/60 hover:border-slate-600 rounded"
                                >
                                    Continue as Guest (Offline Play)
                                </button>
                            </motion.div>
                        ) : (
                            /* If logged in or guest: Show standard Dashboard play buttons */
                            <div className="flex flex-col items-center w-full max-w-sm relative z-10">
                                {/* Save Mode Indicator Badge */}
                                <div className="w-full mb-6 p-4 rounded bg-[#1e293b]/80 border border-slate-800 text-left">
                                    {user ? (
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
                                                <Cloud size={16} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <span className="text-[9px] font-mono leading-none tracking-wider text-emerald-400 font-bold uppercase">Cloud Storage Active</span>
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                                                </div>
                                                <p className="text-[10px] mobile:text-[11px] font-mono leading-none text-slate-400 truncate mt-0.5">
                                                    {user.email}
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleLogOut}
                                                className="px-2.5 py-1 text-slate-400 hover:text-red-400 bg-slate-900 border border-slate-700 hover:border-slate-600 rounded text-[9px] font-mono uppercase transition-colors"
                                            >
                                                LogOut
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2.5">
                                            <div className="flex items-center gap-2.5">
                                                <div className="w-8 h-8 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center text-yellow-500 shrink-0">
                                                    <Laptop size={16} />
                                                </div>
                                                <div className="flex-1">
                                                    <span className="text-[9px] font-mono text-yellow-500 font-bold uppercase tracking-wider block leading-none">Local Guest Mode</span>
                                                    <p className="text-[10px] font-mono text-slate-500 leading-none mt-1">
                                                        album local storage session active.
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => { setIsGuest(false); setAuthTab('signin'); }}
                                                className="w-full mt-1 py-1.5 bg-blue-600/10 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/20 hover:border-blue-500 text-[9px] font-mono uppercase tracking-widest rounded transition-all font-bold"
                                            >
                                                Link to Firebase Cloud Save
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {pct > 0 && (
                                    <div className="w-full max-w-xs mb-8">
                                        <div className="flex justify-between text-[10px] font-mono text-slate-500 mb-1.5 uppercase tracking-widest">
                                            <span>{t('albumProgress')}</span>
                                            <span className="text-emerald-400">{owned.size}/{ALL_STICKERS.length}</span>
                                        </div>
                                        <div className="h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${pct}%` }}
                                                className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.8)]"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-col items-center gap-4">
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <button
                                            onClick={() => setView('album')}
                                            className="bg-[#1e293b] border border-slate-700 text-white font-mono text-xs px-10 py-3 rounded tracking-wider hover:bg-slate-800 transition-all shadow-xl uppercase font-bold cursor-pointer"
                                        >
                                            {t('enterAlbum')}
                                        </button>
                                        <button
                                            onClick={doOpen}
                                            disabled={packs <= 0}
                                            className={`bg-blue-600 text-white font-mono text-xs px-10 py-3 rounded tracking-wider hover:bg-blue-500 transition-all shadow-xl shadow-blue-500/20 uppercase font-bold cursor-pointer ${packs <= 0 ? 'opacity-30' : ''}`}
                                        >
                                            {t('openPack')} ({packs})
                                        </button>
                                    </div>

                                    <footer className="mt-16 flex items-center gap-6 opacity-30">
                                        <div className="flex items-center gap-1.5 text-[10px] font-mono">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                            {t('appActive')}
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[10px] font-mono">
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                            {t('cloudStorageConnected')}
                                        </div>
                                    </footer>
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}

                {view === 'album' && (
                    <motion.div
                        key="album"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}

                        className="max-w-6xl mx-auto p-4 sm:p-10 pb-20 md:pb-10"
                    >
                        {/* Specials Section */}
                        <section className="mb-16">
                            <div className="flex items-center gap-3 mb-6 px-4">
                                <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                                <h3 className="text-xs font-bold text-slate-100 uppercase tracking-[0.2em] font-mono">
                                    {t('specialStickers')}
                                </h3>
                            </div>
                            <div className="bg-[#1e293b] border border-slate-800 rounded-lg p-6">
                                <div className="flex flex-wrap gap-4">
                                    {ALL_STICKERS.filter(s => s.type === 'special').map(s => (
                                        owned.has(s.id)
                                            ? <StickerCard key={s.id} sticker={s} small duplicateCount={duplicates.filter(d => d === s.id).length} />
                                            : <EmptySlot key={s.id} num={s.num} small />
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Confederations */}
                        {CONF_ORDER.map(conf => {
                            const teams = TEAMS.filter(t => t.conf === conf);
                            const meta = CONF_META[conf];
                            if (teams.length === 0) return null;

                            return (
                                <section key={conf} className="mb-16">
                                    <div className="flex items-center gap-3 mb-6 px-4">
                                        <div className="w-1.5 h-6 rounded-full" style={{ backgroundColor: meta.color }}></div>
                                        <h3 className="text-xs font-bold text-slate-200 uppercase tracking-[0.2em] font-mono">
                                            {meta.label}
                                        </h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {teams.map(team => {
                                            const ids = [`${team.id}_B`, ...team.players.map((_, i) => `${team.id}_P${i}`)];
                                            const gotCount = ids.filter(id => owned.has(id)).length;
                                            const isComplete = gotCount === ids.length;

                                            return (
                                                <motion.div
                                                    key={team.id}
                                                    whileHover={{ y: -2 }}
                                                    onClick={() => { setSelectedTeam(team.id); setView('team'); }}
                                                    className={`bg-[#1e293b] border rounded p-5 cursor-pointer transition-all ${isComplete ? 'border-emerald-500/30' : 'border-slate-800 hover:border-slate-700'}`}
                                                >
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div className="flex items-center gap-3">
                                                            <img src={`https://flagcdn.com/w40/${team.flag}.png`} alt="" className="w-6 h-4 object-cover rounded-sm shadow-sm" referrerPolicy="no-referrer" crossOrigin="anonymous" />
                                                            <div className="flex flex-col">
                                                                <h4 className="font-bold text-xs text-white uppercase tracking-tight flex items-center gap-1.5">
                                                                    <span>{team.name}</span>
                                                                </h4>
                                                                <p className="text-[9px] font-mono text-slate-500 tracking-tighter uppercase">{gotCount}/{ids.length} Sync_Status</p>
                                                            </div>
                                                        </div>
                                                        {isComplete && <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />}
                                                    </div>

                                                    <div className="h-1 w-full bg-slate-900 rounded-full overflow-hidden mb-4 border border-white/5">
                                                        <div
                                                            className="h-full transition-all duration-700"
                                                            style={{
                                                                width: `${(gotCount / ids.length) * 100}%`,
                                                                backgroundColor: team.c1
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="flex flex-wrap gap-2 py-2 border-t border-slate-800">
                                                        {ids.slice(0, 3).map(id => (
                                                            owned.has(id)
                                                                ? <StickerCard key={id} sticker={STICKER_MAP[id]} small duplicateCount={duplicates.filter(x => x === id).length} />
                                                                : <EmptySlot key={id} num={STICKER_MAP[id].num} small />
                                                        ))}
                                                        {ids.length > 3 && (
                                                            <div className="w-[62px] h-[84px] rounded border border-slate-800 flex items-center justify-center bg-slate-900/50 text-[9px] font-mono text-slate-500 font-bold">
                                                                +{ids.length - 3}
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </section>
                            );
                        })}
                    </motion.div>
                )}

                {view === 'team' && selectedTeam && (
                    <div className="bg-[#0f172a] min-h-[calc(100vh-56px)]">
                        <TeamPage
                            teamId={selectedTeam}
                            owned={owned}
                            duplicates={duplicates}
                            onBack={() => setView('album')}
                            onNext={(id) => setSelectedTeam(id)}
                            packs={packs}
                            onOpenPack={doOpen}
                            user={user}
                            lang={lang}
                        />
                    </div>
                )}

                {view === 'pack' && (
                    <motion.div
                        key="pack"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center justify-center min-h-[calc(100vh-56px)] p-6 bg-[#0f172a] relative overflow-hidden"
                    >
                        {/* Geometric Accents for Pack Opening */}
                        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                            <div className="absolute top-[20%] left-[-5%] w-[35%] h-[35%] rounded-full bg-blue-600 blur-[130px]"></div>
                            <div className="absolute bottom-[10%] right-[-5%] w-[40%] h-[40%] rounded-full bg-emerald-600 blur-[150px]"></div>
                        </div>

                        <div className="text-center mb-10 p-6 bg-[#1e293b] border border-slate-800 rounded-lg shadow-xl min-w-[300px] relative z-20 backdrop-blur-md">
                            <h2 className="font-mono text-xs font-bold tracking-[0.2em] text-blue-500 mb-2 uppercase">{t('preparingPack')}</h2>
                            <p className="text-[20px] font-impact tracking-widest text-white uppercase">{t('packContents')}</p>
                        </div>

                        <div className="flex flex-wrap justify-center gap-6 mb-16 max-w-4xl relative z-10">
                            {packCards.map((s, i) => (
                                <div
                                    key={i}
                                    className="perspective-1000 cursor-pointer"
                                    onClick={() => {
                                        if (!flipped.has(i)) {
                                            setFlipped(prev => new Set(prev).add(i));
                                        }
                                    }}
                                >
                                    <motion.div
                                        animate={{ rotateY: flipped.has(i) ? 180 : 0 }}
                                        transition={{ type: 'spring', stiffness: 150, damping: 25 }}
                                        className="relative w-[110px] h-[150px] preserve-3d"
                                    >
                                        <div className="absolute inset-0 backface-hidden bg-slate-900 border-2 border-blue-500/20 rounded shadow-2xl flex flex-col items-center justify-center overflow-hidden">
                                            {/* Grid pattern for card back */}
                                            <div className="absolute inset-0 opacity-10 pointer-events-none"
                                                 style={{ backgroundImage: 'radial-gradient(rgba(59, 130, 246, 0.4) 1px, transparent 1px)', backgroundSize: '10px 10px' }}>
                                            </div>
                                            <div className="w-12 h-12 border border-blue-500/40 rounded-sm flex items-center justify-center text-blue-400 font-mono text-sm bg-blue-500/5 relative z-10">
                                                ?
                                            </div>
                                            <span className="font-mono text-[7px] tracking-[0.2em] text-slate-400 mt-5 relative z-10 uppercase font-black">{t('tapToReveal')}</span>
                                            <div className="absolute bottom-1 left-1 right-1 h-0.5 bg-blue-500/20 rounded-full"></div>
                                        </div>
                                        <div className="absolute inset-0 backface-hidden rotate-y-180 flex items-center justify-center">
                                            <StickerCard sticker={s} />
                                            {collected && (
                                                <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded-sm text-[8px] font-mono font-bold uppercase text-white shadow-lg z-30 ${!owned.has(s.id) ? 'bg-emerald-500 shadow-emerald-500/20' : 'bg-slate-700'}`}>
                                                    {!owned.has(s.id) ? 'NEW' : 'DUP'}
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </div>

                        {/* Real-time Handshake / Sync Feedback Banner */}
                        {collected && (
                            <div className="mb-6 w-full max-w-sm text-center">
                                {isSyncing ? (
                                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-950/40 border border-blue-800/40 rounded text-[10px] font-mono font-bold text-blue-400 uppercase tracking-wider">
                                        <RefreshCw size={11} className="animate-spin" />
                                        Synchronizing progress metrics with Firebase Cloud...
                                    </div>
                                ) : syncSuccess === true ? (
                                    <div className="inline-flex flex-col items-center gap-1.5 px-4 py-2.5 bg-emerald-950/30 border border-emerald-800/40 rounded text-[10px] font-mono text-emerald-300 w-full text-left">
                                        <div className="flex items-center gap-1.5 font-bold uppercase text-emerald-400">
                                            <CheckCircle2 size={12} className="shrink-0" />
                                            <span>{t('syncedSuccess')}</span>
                                        </div>
                                        {user ? (
                                            <p className="text-slate-400 text-[9px] font-normal leading-normal">
                                                Your album status has been written to Firebase under ID: <code className="text-blue-300 select-all font-bold">{user.uid}</code>
                                            </p>
                                        ) : (
                                            <p className="text-slate-400 text-[9px] font-normal leading-normal">
                                                Saved locally in local storage. Connect database in cover screen to unlock cross-device synchronization.
                                            </p>
                                        )}
                                    </div>
                                ) : syncSuccess === false ? (
                                    <div className="inline-flex flex-col items-center gap-1.5 px-4 py-2 bg-red-950/30 border border-red-800/40 rounded text-[10px] font-mono text-red-300 w-full text-left">
                                        <div className="flex items-center gap-1.5 font-bold uppercase text-red-400">
                                            <AlertCircle size={12} className="shrink-0" />
                                            <span>{t('syncFailed')}</span>
                                        </div>
                                        <p className="text-slate-400 text-[9px] font-normal leading-normal">
                                            Could not write progress. Proceeding with offline local fallback.
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        )}

                        <div className="flex gap-4">
                            {!collected ? (
                                <>
                                    <button
                                        onClick={() => setFlipped(new Set([0, 1, 2, 3, 4]))}
                                        disabled={isSyncing}
                                        className="px-8 py-2 bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold uppercase tracking-wider rounded hover:bg-slate-700 transition-colors disabled:opacity-50"
                                    >
                                        {t('revealAll')}
                                    </button>
                                    <button
                                        onClick={doCollect}
                                        disabled={isSyncing}
                                        className="px-10 py-2 bg-blue-600 text-white text-xs uppercase tracking-wider rounded shadow-lg shadow-blue-500/20 hover:bg-blue-500 transition-all font-bold flex items-center gap-2 cursor-pointer disabled:opacity-50"
                                    >
                                        {isSyncing ? (
                                            <>
                                                <RefreshCw className="animate-spin" size={12} />
                                                Syncing...
                                            </>
                                        ) : (
                                            t('saveToAlbum')
                                        )}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setView('album')}
                                        disabled={isSyncing}
                                        className="px-10 py-2 bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold uppercase tracking-wider rounded hover:bg-slate-700 disabled:opacity-50"
                                    >
                                        {t('backToAlbum')}
                                    </button>
                                    <button
                                        onClick={doOpen}
                                        disabled={packs <= 0 || isSyncing}
                                        className={`px-10 py-2 bg-blue-600 text-white text-xs uppercase tracking-wider rounded shadow-lg transition-all ${packs > 0 && !isSyncing ? 'hover:bg-blue-500' : 'opacity-20 cursor-not-allowed'}`}
                                    >
                                        {t('openAnotherPack')} ({packs})
                                    </button>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}

                {view === 'profile' && (
                    <motion.div
                        key="profile"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.25 }}
                        className="bg-[#0f172a] min-h-[calc(100vh-56px)] pb-16 md:pb-0"
                    >
                        <UserProfile
                            user={user}
                            owned={owned}
                            duplicates={duplicates}
                            packs={packs}
                            connectOpen={connectOpen}
                            onToggleConnect={async (val) => {
                                setConnectOpen(val);
                                if (user) {
                                    const docRef = doc(db, 'users', user.uid);
                                    await setDoc(docRef, {
                                        connectOpen: val,
                                        updatedAt: new Date().toISOString()
                                    }, { merge: true });
                                }
                            }}
                            onLogOut={handleLogOut}
                            onResetProgress={handleResetProgress}
                            onBack={() => setView('album')}
                            onUpdateUser={(updatedUser) => setUser(updatedUser)}
                            onAddPacks={async (amount) => {
                                setPacks(p => p + amount);
                            }}
                            onUpdateInventory={async (newOwned, newDuplicates, newPacks) => {
                                setOwned(newOwned);
                                setDuplicates(newDuplicates);
                                if (typeof newPacks === 'number') {
                                    setPacks(newPacks);
                                }
                            }}
                            notificationPermission={notificationPermission}
                            onRequestNotificationPermission={handleRequestNotificationPermission}
                            streak={streak}
                            achievements={achievements}
                            lang={lang}
                        />
                    </motion.div>
                )}

                {view === 'friends' && (
                    <motion.div
                        key="friends"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.25 }}
                        className="bg-[#0f172a] min-h-[calc(100vh-56px)] pb-16 md:pb-0"
                    >
                        <FriendsPage
                            user={user}
                            owned={owned}
                            duplicates={duplicates}
                            onBack={() => {
                                setFriendsPageDefaultTab('social');
                                setView('album');
                            }}
                            onUpdateInventory={async (newOwned, newDuplicates) => {
                                setOwned(newOwned);
                                setDuplicates(newDuplicates);
                            }}
                            defaultTab={friendsPageDefaultTab}
                            lang={lang}
                        />
                    </motion.div>
                )}

                {view === 'shop' && (
                    <motion.div
                        key="shop"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.25 }}
                        className="bg-[#0f172a] min-h-[calc(100vh-56px)] pb-16 md:pb-0"
                    >
                        <StickerShop
                            onBack={() => setView('album')}
                            onAddPacks={async (amount) => {
                                setPacks(p => p + amount);
                            }}
                            packs={packs}
                            lang={lang}
                            user={user}
                        />
                    </motion.div>
                )}

                {view === 'groups' && (
                    <motion.div
                        key="groups"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.25 }}
                        className="bg-[#0f172a] min-h-[calc(100vh-56px)] pb-16 md:pb-0"
                    >
                        <GroupsPage
                            user={user}
                            onSelectTeam={(teamId) => {
                                setSelectedTeam(teamId);
                                setView('team');
                            }}
                            lang={lang}
                        />
                    </motion.div>
                )}

                {view === 'schedules' && (
                    <motion.div
                        key="schedules"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.25 }}
                        className="bg-[#0f172a] min-h-[calc(100vh-56px)] pb-16 md:pb-0"
                    >
                        <SchedulesPage
                            user={user}
                            packs={packs}
                            setPacks={setPacks}
                            predictions={predictions}
                            savePrediction={async (matchId, pred) => {
                                if (!user) {
                                    return;
                                }
                                setPredictions(prev => {
                                    const copy = { ...prev };
                                    if (pred === null || pred === undefined) {
                                        delete copy[matchId];
                                    } else {
                                        copy[matchId] = pred;
                                    }
                                    return copy;
                                });

                                if (user) {
                                    try {
                                        const predictionDocId = `${user.uid}_${matchId}`;
                                        const predRef = doc(db, 'predictions', predictionDocId);
                                        if (pred === null || pred === undefined) {
                                            await deleteDoc(predRef);
                                        } else {
                                            const predictionPayload: any = {
                                                id: predictionDocId,
                                                userId: user.uid,
                                                matchId: matchId,
                                                scoreA: Number(pred.scoreA),
                                                scoreB: Number(pred.scoreB),
                                                goalsOverUnder: pred.goalsOverUnder || (Number(pred.scoreA) + Number(pred.scoreB) > 2 ? 'over' : 'under'),
                                                corners: Number(pred.corners || 0),
                                                submitted: typeof pred.submitted === 'boolean' ? pred.submitted : true,
                                                resolved: typeof pred.resolved === 'boolean' ? pred.resolved : false,
                                                status: pred.status || 'active',
                                                displayName: user.displayName || user.email?.split('@')[0] || 'Collector',
                                                photoURL: user.photoURL || 'wc_ball',
                                                email: user.email || '',
                                                updatedAt: new Date().toISOString()
                                            };
                                            if (pred.actualScoreA !== undefined && pred.actualScoreA !== null) {
                                                predictionPayload.actualScoreA = Number(pred.actualScoreA);
                                            }
                                            if (pred.actualScoreB !== undefined && pred.actualScoreB !== null) {
                                                predictionPayload.actualScoreB = Number(pred.actualScoreB);
                                            }
                                            if (pred.actualCorners !== undefined && pred.actualCorners !== null) {
                                                predictionPayload.actualCorners = Number(pred.actualCorners);
                                            }
                                            if (pred.packsWon !== undefined && pred.packsWon !== null) {
                                                predictionPayload.packsWon = Number(pred.packsWon);
                                            }
                                            await setDoc(predRef, predictionPayload, { merge: true });
                                        }
                                    } catch (err) {
                                        console.error("Firestore single-prediction save failed: ", err);
                                        handleFirestoreError(err, OperationType.UPDATE, `predictions/${user.uid}_${matchId}`);
                                    }
                                }
                            }}
                            lang={lang}
                        />
                    </motion.div>
                )}

                {view === 'quiz' && (
                    <motion.div
                        key="quiz"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.25 }}
                        className="bg-[#0f172a] min-h-[calc(100vh-56px)] pb-16 md:pb-0"
                    >
                        <FootballQuiz
                            user={user}
                            packs={packs}
                            setPacks={setPacks}
                            lang={lang}
                            userQuizHistory={quizHistory}
                            onSaveQuizResult={handleSaveQuizResult}
                            onClaimShareReward={handleClaimShareReward}
                            onResetQuiz={handleResetQuiz}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Footer Info */}
            {view !== 'cover' && view !== 'pack' && (
                <footer className="hidden sm:flex h-8 bg-blue-600 text-white px-6 items-center justify-between text-[11px] font-mono sticky bottom-0 z-50">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-1.5">
                            <span className="text-emerald-300">✔</span> {t('savedSecurely')}
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 opacity-80">
                            <span className="text-blue-200">⚡</span> {t('activeOnline')}
                        </div>
                    </div>
                    <div className="flex items-center gap-4 uppercase tracking-tighter text-[9px] font-black">
                        <span>COLLECTED: {owned.size} / {ALL_STICKERS.length}</span>
                        <span>UTF-8</span>
                    </div>
                </footer>
            )}
            <AIAgentBot />
            <Footer lang={lang} />
        </div>
    );
}

function TeamPage({ teamId, owned, duplicates = [], onBack, onNext, packs, onOpenPack, user, lang }: {
    teamId: string;
    owned: Set<string>;
    duplicates?: string[];
    onBack: () => void;
    onNext: (id: string) => void;
    packs: number;
    onOpenPack: () => void;
    user: any;
    lang: LanguageCode;
}) {
    const t = (key: string) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS['en']?.[key] || key;
    const team = TEAMS.find(t => t.id === teamId)!;
    const currentIdx = TEAMS.findIndex(t => t.id === teamId);
    const prevTeam = TEAMS[currentIdx - 1];
    const nextTeam = TEAMS[currentIdx + 1];

    const badgeId = `${team.id}_B`;
    const playerIds = team.players.map((_, i) => `${team.id}_P${i}`);
    const allIds = [badgeId, ...playerIds];
    const gotCount = allIds.filter(id => owned.has(id)).length;
    const isComplete = gotCount === allIds.length;

    const [selectedSticker, setSelectedSticker] = useState<Sticker | null>(null);
    const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

    const stickerCardRef = useRef<HTMLDivElement>(null);
    const [shareUrl, setShareUrl] = useState<string | null>(null);
    const [generatingImage, setGeneratingImage] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    // Restart generation state whenever selectedSticker changes
    useEffect(() => {
        setShareUrl(null);
        setUploadError(null);
        if (selectedSticker) {
            const autoGenerate = async () => {
                // Yield momentarily to let layout complete rendering
                await new Promise(r => setTimeout(r, 600));
                if (selectedSticker && stickerCardRef.current) {
                    setGeneratingImage(true);
                    try {
                        const dataUrl = await toPng(stickerCardRef.current, {
                            backgroundColor: '#0c1020',
                            cacheBust: true,
                            style: {
                                transform: 'scale(1)',
                                transformOrigin: 'top left',
                            }
                        });
                        const storageRefInstance = sRef(storage, `shared_stickers/${selectedSticker.id}_${Date.now()}.png`);
                        await uploadString(storageRefInstance, dataUrl, 'data_url');
                        const downloadUrl = await getDownloadURL(storageRefInstance);
                        setShareUrl(downloadUrl);
                    } catch (e) {
                        console.error("Background PNG renderer error:", e);
                        const errMsg = e instanceof Error ? e.message : String(e);
                        setUploadError(errMsg);
                    } finally {
                        setGeneratingImage(false);
                    }
                }
            };
            autoGenerate();
        } else {
            setGeneratingImage(false);
        }
    }, [selectedSticker]);

    const getShareLink = async (sticker: Sticker): Promise<string> => {
        if (shareUrl) return shareUrl;

        if (generatingImage) {
            let attempts = 0;
            while (generatingImage && !shareUrl && attempts < 50) {
                await new Promise(r => setTimeout(r, 100));
                attempts++;
            }
            if (shareUrl) return shareUrl;
        }

        setGeneratingImage(true);
        setUploadError(null);
        try {
            if (!stickerCardRef.current) return window.location.href;
            const dataUrl = await toPng(stickerCardRef.current, {
                backgroundColor: '#0c1020',
                cacheBust: true,
                style: {
                    transform: 'scale(1)',
                    transformOrigin: 'top left',
                }
            });
            const storageRefInstance = sRef(storage, `shared_stickers/${sticker.id}_${Date.now()}.png`);
            await uploadString(storageRefInstance, dataUrl, 'data_url');
            const downloadUrl = await getDownloadURL(storageRefInstance);
            setShareUrl(downloadUrl);
            setGeneratingImage(false);
            return downloadUrl;
        } catch (e) {
            console.error("Direct capture error:", e);
            const errMsg = e instanceof Error ? e.message : String(e);
            setUploadError(errMsg);
            setGeneratingImage(false);
            return window.location.href;
        }
    };

    const handleShareSystem = async (sticker: Sticker) => {
        const url = await getShareLink(sticker);
        const text = `Check out my sticker ${sticker.name} (Code: CODE_${String(sticker.num).padStart(3, '0')}) from ${team.name} in the Paganini WC 2026 Album! ⚽🏆`;
        const shareData = {
            title: 'Paganini WC 2026 Album Sticker',
            text: text,
            url: url,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch (err) {
                console.log('System share failed, falling back to manual: ', err);
            }
        }
        await handleCopyLink(sticker);
    };

    const handleCopyLink = async (sticker: Sticker) => {
        const url = await getShareLink(sticker);
        const text = `I got ${sticker.name} (Code: CODE_${String(sticker.num).padStart(3, '0')}) from ${team.name} in my Paganini World Cup 2026 Album! Direct Image URL: ${url}`;
        navigator.clipboard.writeText(text);
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus('idle'), 2000);
    };

    const handleSocialShare = async (platform: 'x' | 'whatsapp' | 'telegram', sticker: Sticker) => {
        const url = await getShareLink(sticker);
        let href = '';

        if (platform === 'x') {
            href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                `Check out my Paganini World Cup sticker: ${sticker.name} (${team.name}) ⚽!`
            )}&url=${encodeURIComponent(url)}`;
        } else if (platform === 'whatsapp') {
            href = `https://api.whatsapp.com/send?text=${encodeURIComponent(
                `I just collected ${sticker.name} (${team.name}) in my Paganini World Cup 2026 Album ⚽! Direct Image: ${url}`
            )}`;
        } else if (platform === 'telegram') {
            href = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
                `I collected ${sticker.name} from ${team.name} in Paganini WC Album! 🏆`
            )}`;
        }

        window.open(href, '_blank', 'noopener,noreferrer');
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="max-w-5xl mx-auto pb-20 md:pb-10"
        >
            {/* Team Header */}
            <div className="relative overflow-hidden p-4 sm:p-12 border-b border-slate-800 bg-[#1e293b]">
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 relative z-10">
                    <img src={`https://flagcdn.com/w160/${team.flag}.png`} alt="" className="w-32 h-20 object-cover rounded shadow-2xl" referrerPolicy="no-referrer" crossOrigin="anonymous" />
                    <div className="text-center sm:text-left flex-1">
                        <p className="text-[10px] font-bold font-mono tracking-[0.4em] uppercase mb-4" style={{ color: CONF_META[team.conf].color }}>
                            {t('confederation')}: {team.conf}
                        </p>
                        <h2 className="font-impact text-4xl sm:text-7xl uppercase tracking-tighter mb-4 leading-none text-white">
                            {team.name}
                        </h2>
                        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6">
                            <div className="flex items-center gap-4">
                                <div className="w-40 h-1.5 bg-slate-900 rounded-full overflow-hidden border border-white/5">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(gotCount / allIds.length) * 100}%` }}
                                        className="h-full bg-blue-500"
                                    />
                                </div>
                                <span className="text-[10px] font-mono font-bold text-slate-500">{gotCount}/{allIds.length} {t('collected')}</span>
                            </div>
                            {isComplete && (
                                <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {t('teamCompleted')}
                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Team Nav */}
                <div className="flex justify-between items-center mt-10 max-w-md mx-auto sm:mx-0">
                    <button onClick={onBack} className="flex items-center gap-2 text-[10px] font-mono font-bold text-slate-500 hover:text-white uppercase transition-colors font-sans">
                        <ChevronLeft size={14} /> {t('backToAlbum')}
                    </button>
                    <div className="flex gap-6">
                        {prevTeam && (
                            <button onClick={() => onNext(prevTeam.id)} className="text-[10px] font-mono font-bold text-slate-500 hover:text-blue-400 uppercase">
                                {t('prevTeam')}
                            </button>
                        )}
                        {nextTeam && (
                            <button onClick={() => onNext(nextTeam.id)} className="text-[10px] font-mono font-bold text-slate-500 hover:text-blue-400 uppercase">
                                {t('nextTeam')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4 sm:p-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-16">
                    {/* Badge & Info */}
                    <div className="col-span-1 border-r border-slate-800 pr-12 hidden md:block">
                        <h3 className="text-[10px] font-mono font-bold text-slate-600 uppercase tracking-[0.3em] mb-8">Node_Icon</h3>
                        <div className="flex flex-col items-center">
                            {owned.has(badgeId) ? <StickerCard sticker={STICKER_MAP[badgeId]} duplicateCount={duplicates.filter(x => x === badgeId).length} onClick={() => setSelectedSticker(STICKER_MAP[badgeId])} /> : <EmptySlot num={STICKER_MAP[badgeId].num} />}
                            <p className="mt-6 text-[10px] text-center text-slate-500 font-mono font-bold uppercase tracking-widest leading-relaxed">
                                {team.name}<br/>Registry_Identifier
                            </p>
                        </div>
                    </div>

                    {/* SQUAD */}
                    <div className="col-span-1 md:col-span-3">
                        <h3 className="text-[10px] font-mono font-bold text-slate-600 uppercase tracking-[0.4em] mb-10 text-center sm:text-left">Personnel_Registry</h3>
                        <div className="flex flex-wrap gap-3 sm:gap-6 justify-center sm:justify-start">
                            <div className="md:hidden">
                                {owned.has(badgeId) ? <StickerCard sticker={STICKER_MAP[badgeId]} duplicateCount={duplicates.filter(x => x === badgeId).length} onClick={() => setSelectedSticker(STICKER_MAP[badgeId])} /> : <EmptySlot num={STICKER_MAP[badgeId].num} />}
                            </div>
                            {playerIds.map(id => (
                                owned.has(id)
                                    ? <StickerCard key={id} sticker={STICKER_MAP[id]} duplicateCount={duplicates.filter(x => x === id).length} onClick={() => setSelectedSticker(STICKER_MAP[id])} />
                                    : <EmptySlot key={id} num={STICKER_MAP[id].num} />
                            ))}
                        </div>

                        {!isComplete && (
                            <div className="mt-20 p-10 rounded-lg bg-slate-900/40 border border-slate-800 text-center">
                                <p className="text-slate-500 text-[10px] font-mono mb-8 font-bold tracking-tight uppercase">
                                    Analysis indicates {allIds.length - gotCount} missing nodes. Database incomplete.
                                </p>
                                <button
                                    onClick={onOpenPack}
                                    disabled={packs <= 0}
                                    className={`bg-blue-600 text-white px-10 py-3 rounded font-impact tracking-widest text-sm uppercase transition-all shadow-xl shadow-blue-500/20 ${packs > 0 ? 'hover:bg-blue-500 hover:scale-105' : 'opacity-20'}`}
                                >
                                    Acquire_Resources ({packs})
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Bigger popup and Share controls */}
            <AnimatePresence>
                {selectedSticker && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md"
                        onClick={() => setSelectedSticker(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close Button */}
                            <button
                                onClick={() => setSelectedSticker(null)}
                                className="absolute top-4 right-4 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-800/90 rounded-full p-2 transition-colors z-20"
                            >
                                <X size={16} />
                            </button>

                            <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-8 items-center">
                                {/* Visual Representation */}
                                <div className="flex-shrink-0">
                                    <div
                                        ref={stickerCardRef}
                                        className={`w-44 h-60 rounded-xl flex flex-col items-center justify-between p-4 relative overflow-hidden select-none border-2 shadow-2xl ${
                                            selectedSticker.rarity === 'legendary'
                                                ? 'border-blue-500/80 shadow-[0_0_25px_rgba(59,130,246,0.5)]'
                                                : selectedSticker.rarity === 'rare'
                                                    ? 'border-emerald-500/80 shadow-[0_0_25px_rgba(16,185,129,0.4)]'
                                                    : 'border-slate-600/50'
                                        }`}
                                        style={
                                            selectedSticker.teamId
                                                ? { backgroundImage: `linear-gradient(150deg, ${team.c1}ee, ${team.c1}ad, ${team.c2}33)` }
                                                : selectedSticker.rarity === 'legendary'
                                                    ? { backgroundImage: 'linear-gradient(150deg, #1e1b4b, #312e81, #1e1b4b)' }
                                                    : selectedSticker.rarity === 'rare'
                                                        ? { backgroundImage: 'linear-gradient(150deg, #111827, #1f2937, #111827)' }
                                                        : { backgroundColor: '#1e293b' }
                                        }
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none" />

                                        {/* Holographic Gloss Sheen Sweep */}
                                        <div className="hologram-gloss pointer-events-none z-10" />

                                        <span className="absolute top-1.5 left-2 text-[8px] text-emerald-400 font-mono font-bold tracking-tighter flex items-center gap-0.5 z-10">
                      <span className="text-[6px] bg-emerald-500/20 px-0.8 py-0.3 rounded text-emerald-300 font-black">NFT</span>
                      #{String(selectedSticker.num).padStart(3, '0')}
                    </span>

                                        <div className="absolute top-1.5 right-2 flex gap-1 opacity-90 z-10">
                                            {selectedSticker.rarity === 'legendary' && <div className="w-2 h-2 rounded-full bg-blue-400 shadow-[0_0_6px_#3b82f6]" />}
                                            {selectedSticker.rarity === 'rare' && <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_#10b981]" />}
                                        </div>

                                        <div className="flex-1 flex items-center justify-center">
                                            {selectedSticker.type === 'special' && <span className="text-5xl filter drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">{selectedSticker.icon}</span>}
                                            {selectedSticker.type === 'badge' && team?.flag && (
                                                <img src={`https://flagcdn.com/w160/${team.flag}.png`} alt={team.name} className="w-20 h-14 object-cover rounded shadow-lg" referrerPolicy="no-referrer" crossOrigin="anonymous" />
                                            )}
                                            {selectedSticker.type === 'player' && (
                                                <span className="text-5xl filter drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">{selectedSticker.pos ? POS_ICON[selectedSticker.pos] : '⚽'}</span>
                                            )}
                                        </div>

                                        {selectedSticker.type === 'player' && team?.flag && (
                                            <img
                                                src={`https://flagcdn.com/w40/${team.flag}.png`}
                                                alt=""
                                                className="absolute top-8 right-2 w-5 h-3.5 object-cover rounded shadow-md opacity-90 z-10"
                                                referrerPolicy="no-referrer"
                                                crossOrigin="anonymous"
                                            />
                                        )}

                                        {selectedSticker.type === 'player' && selectedSticker.pos && (
                                            <span className="absolute bottom-11 rounded px-1.5 py-0.5 text-[8px] text-white font-mono font-bold tracking-widest uppercase bg-slate-900/95 border border-slate-700 z-10">
                        {selectedSticker.pos}
                      </span>
                                        )}

                                        {duplicates.filter(d => d === selectedSticker.id).length > 0 && (
                                            <div className="absolute bottom-10 right-2 bg-amber-500 text-slate-950 font-mono font-black rounded border border-amber-400 shadow-md text-[8px] px-1.5 py-0.5 z-10">
                                                x{duplicates.filter(d => d === selectedSticker.id).length + 1}
                                            </div>
                                        )}

                                        <div className="w-full bg-slate-950/60 backdrop-blur-md py-1.5 px-2 flex items-center justify-center border-t border-white/10 gap-1 rounded-b z-10">
                                            {team?.flag && (
                                                <img src={`https://flagcdn.com/w20/${team.flag}.png`} alt="" className="w-3.5 h-2.5 object-cover rounded-[1px]" referrerPolicy="no-referrer" crossOrigin="anonymous" />
                                            )}
                                            <span className="text-[9px] text-white font-black uppercase tracking-wider text-center truncate select-none">
                        {selectedSticker.name}
                      </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Info and Share options */}
                                <div className="flex-1 w-full text-left">
                  <span className="text-[9px] font-mono font-bold text-slate-500 uppercase tracking-widest block mb-1">
                    Decentralized NFT Asset
                  </span>
                                    <h4 className="text-xl sm:text-2xl font-impact uppercase tracking-tight text-white mb-1">
                                        {selectedSticker.name}
                                    </h4>
                                    <p className="text-[10px] text-slate-400 font-mono mb-2">
                                        Country: <span className="text-blue-400 font-bold">{team.name}</span> • Rarity: <span className="capitalize font-bold" style={{ color: selectedSticker.rarity === 'legendary' ? '#60a5fa' : selectedSticker.rarity === 'rare' ? '#34d399' : '#94a3b8' }}>{selectedSticker.rarity}</span>
                                    </p>

                                    {/* Blockchain Ledger Subsystem Infobox */}
                                    <div className="bg-slate-950 border border-slate-800/80 rounded p-2.5 space-y-1.5 text-[9px] font-mono leading-relaxed mb-3">
                    <span className="text-[8px] text-emerald-400 font-bold uppercase tracking-[0.15em] block">
                      ⭐ OFFICIAL COLLECTIBLE DETAILS
                    </span>
                                        <div className="grid grid-cols-2 gap-y-1 text-slate-400">
                                            <div>Sticker Type:</div>
                                            <div className="text-right text-slate-200">World Cup Special</div>

                                            <div>Verified Album:</div>
                                            <div className="text-right text-blue-400 font-bold">Paganini 2026 App</div>

                                            <div>Unique Serial ID:</div>
                                            <div className="text-right text-emerald-400 font-black">STK6-ID-{String(selectedSticker.num).padStart(4, '0')}</div>

                                            <div>Verified Owner:</div>
                                            <div className="text-right text-slate-300 truncate max-w-[130px]" title={user ? `Wallet representing ${user.email}` : "Virtual custody node"}>
                                                {user ? `Collector #${user.uid.slice(0, 6).toUpperCase()}` : 'Guest Collector'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="h-px bg-slate-800 my-3" />

                                    {generatingImage && (
                                        <div className="flex items-center gap-2 py-2 px-3 bg-blue-500/10 border border-blue-500/20 rounded-lg font-mono text-[9px] text-blue-400 animate-pulse mb-4 justify-center">
                                            <RefreshCw size={11} className="animate-spin" /> Preparing high-definition sticker image...
                                        </div>
                                    )}

                                    {shareUrl && (
                                        <div className="flex items-center gap-2 py-2 px-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg font-mono text-[9px] text-emerald-400 mb-4 justify-center font-bold">
                                            <Check size={11} className="text-emerald-400 font-bold" /> Cloud link compiled successfully! Ready to broadcast.
                                        </div>
                                    )}

                                    {uploadError && (
                                        <div className="py-2.5 px-3 bg-red-500/10 border border-red-500/20 rounded-lg text-[10px] text-red-400 mb-4 font-mono space-y-1 text-left">
                                            <div className="flex items-center gap-1.5 font-bold text-red-400">
                                                <AlertCircle size={12} className="text-red-400" /> Upload Handshake Error
                                            </div>
                                            <div className="text-[9px] text-red-300 break-words opacity-90 leading-relaxed font-mono">
                                                {uploadError}
                                            </div>
                                            {uploadError.toLowerCase().includes('permission') || uploadError.toLowerCase().includes('unauthorized') ? (
                                                <div className="text-[9px] text-slate-400 border-t border-red-500/20 pt-1.5 mt-1 leading-relaxed">
                                                    💡 <span className="text-slate-300 font-bold">Action Needed:</span> Your Firebase Storage bucket security rules are locking access. Please go to the <a href="https://console.firebase.google.com/project/paganini2026-ce3e7/storage" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline hover:text-blue-300">Firebase Storage console</a>, navigate to the <span className="text-slate-200">Rules</span> tab, and replace your ruleset with the following to allow public uploads:
                                                    <pre className="bg-slate-950 p-1.5 rounded mt-1.5 overflow-x-auto text-[8px] text-slate-400 border border-slate-800 font-mono leading-normal">
{`rules_version = '2';
service firebase.storage {
  match /b/{bucket}/o {
    match /shared_stickers/{allPaths=**} {
      allow read, write: if true;
    }
  }
}`}
                          </pre>
                                                </div>
                                            ) : (
                                                <div className="text-[9px] text-slate-400 border-t border-red-500/20 pt-1.5 mt-1 leading-relaxed">
                                                    💡 <span className="text-slate-300 font-bold">Action Needed:</span> Ensure Firebase Storage has been initialized by visiting the <a href="https://console.firebase.google.com/project/paganini2026-ce3e7/storage" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline hover:text-blue-300">Firebase Storage Console</a>. Click <span className="text-slate-200">"Get Started"</span>, choose your location, and finalize initialization!
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <p className="text-[9px] text-slate-500 font-mono uppercase tracking-widest mb-3">
                                        Broadcasting Console
                                    </p>

                                    <div className="space-y-3">
                                        <button
                                            onClick={() => handleShareSystem(selectedSticker)}
                                            disabled={generatingImage}
                                            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white py-2 px-4 rounded text-xs font-bold font-mono transition-transform active:scale-95 shadow-lg shadow-blue-500/10 uppercase cursor-pointer disabled:opacity-50"
                                        >
                                            <Share2 size={13} /> Quick Native Share
                                        </button>

                                        <div className="grid grid-cols-3 gap-2">
                                            <button
                                                onClick={() => handleSocialShare('x', selectedSticker)}
                                                disabled={generatingImage}
                                                className="flex flex-col items-center justify-center gap-1.5 p-2 rounded bg-slate-950/60 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-white text-[9px] font-mono transition-all hover:-translate-y-0.5 cursor-pointer disabled:opacity-50"
                                            >
                                                <span className="text-xs">𝕏</span>
                                                <span>Post</span>
                                            </button>

                                            <button
                                                onClick={() => handleSocialShare('whatsapp', selectedSticker)}
                                                disabled={generatingImage}
                                                className="flex flex-col items-center justify-center gap-1.5 p-2 rounded bg-slate-950/60 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-white text-[9px] font-mono transition-all hover:-translate-y-0.5 cursor-pointer disabled:opacity-50"
                                            >
                                                <span className="text-green-500 text-xs">💬</span>
                                                <span>WhatsApp</span>
                                            </button>

                                            <button
                                                onClick={() => handleSocialShare('telegram', selectedSticker)}
                                                disabled={generatingImage}
                                                className="flex flex-col items-center justify-center gap-1.5 p-2 rounded bg-slate-950/60 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-white text-[9px] font-mono transition-all hover:-translate-y-0.5 cursor-pointer disabled:opacity-50"
                                            >
                                                <span className="text-sky-400 text-xs">✈️</span>
                                                <span>Telegram</span>
                                            </button>
                                        </div>

                                        <button
                                            onClick={() => handleCopyLink(selectedSticker)}
                                            disabled={generatingImage}
                                            className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 text-white py-2 px-4 rounded text-xs font-mono transition-all uppercase cursor-pointer disabled:opacity-50"
                                        >
                                            {copyStatus === 'copied' ? (
                                                <>
                                                    <Check size={13} className="text-emerald-400" /> Transmitted
                                                </>
                                            ) : (
                                                <>
                                                    <Copy size={13} /> Copy Connection String
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
